import {
  SUPPORT_SESSION_PATH,
  type SupportSessionGrant,
  SupportSessionGrantSchema,
} from "@vidimo/shared";
import { markPageReloading } from "@/lib/sign-out-state";

// A support tab (ADR-0143): the web build opened from the CRM as one of a
// customer's staff. Everything the app keeps in the browser is kept in the
// tab: the session in sessionStorage, which survives a reload and goes with
// the tab; every `localStorage` write redirected there too; and the replica in
// memory (lib/powersync.ts). Closing the tab leaves nothing of the customer
// behind, and none of it ever touches the agent's own app in the same browser.

const GRANT_KEY = "vidimo.support-session";
const ENDED_PARAM = "ended";

function readGrant(): SupportSessionGrant | null {
  try {
    const raw = window.sessionStorage.getItem(GRANT_KEY);
    return raw ? SupportSessionGrantSchema.parse(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * The support session this tab was opened for; null in every other tab, on
 * native, and when prerendering. Read once per page load: a tab becomes a
 * support tab, or stops being one, through a full page load.
 */
export const SUPPORT_SESSION: SupportSessionGrant | null =
  typeof window === "undefined" ? null : readGrant();

/** Makes this tab a support tab from its next page load on. */
export function holdSupportSession(grant: SupportSessionGrant): void {
  window.sessionStorage.clear();
  window.sessionStorage.setItem(GRANT_KEY, JSON.stringify(grant));
}

/**
 * Points `localStorage` at the tab's sessionStorage in a support tab. Runs
 * before any other module of the app (main.tsx imports it first), since every
 * reader takes `window.localStorage` at the moment it reads.
 */
export function scopeStorageToSupportTab(): void {
  if (!SUPPORT_SESSION) {
    return;
  }
  Object.defineProperty(window, "localStorage", {
    configurable: true,
    value: window.sessionStorage,
  });
}

/** Where a leaving tab's last writes go: nowhere. */
const DISCARDING_STORAGE: Storage = {
  clear: () => undefined,
  getItem: () => null,
  key: () => null,
  length: 0,
  removeItem: () => undefined,
  setItem: () => undefined,
};

/**
 * Ends the support tab on this device: whatever it kept goes, and the page is
 * replaced, past any unsaved-changes prompt, so nothing stays in memory. The
 * page runs on until the replace lands, so its storage keeps nothing from
 * here on. The server session is ended by the caller (an Exit signs out) or
 * is already over (expired, revoked).
 */
export function leaveSupportSession(): void {
  markPageReloading();
  Object.defineProperty(window, "localStorage", {
    configurable: true,
    value: DISCARDING_STORAGE,
  });
  window.sessionStorage.clear();
  window.location.replace(
    `${SUPPORT_SESSION_PATH}?${new URLSearchParams({ [ENDED_PARAM]: "1" })}`
  );
}

export function isSupportSessionEndedPage(search: string): boolean {
  return new URLSearchParams(search).has(ENDED_PARAM);
}
